import {
  animations,
  colors,
  fontWeights,
  mixins,
  shadows,
} from './theme/default';
import { css } from '@emotion/react';
import { rem } from 'polished';

const stylesObj = {
  'roadmap-elektromobilitaet': {
    global: css`
      body {
        background-color: ${colors.blue100};

        h1,
        h2,
        h3,
        h4,
        h5,
        h6,
        a {
          color: ${colors.blue900};
        }
      }
    `,
    typography: {
      h1: css`
        font-weight: ${fontWeights.medium};
      `,
    },
    link: {
      tile: css`
        background: ${colors.blue700};
      `,
    },
    moduleHeader: {
      mainTitle: css`
        color: ${colors.blue900};
      `,
      leadText: css`
        color: ${colors.black};
      `,
    },
    teaserTools: {
      tool: css`
        background: ${colors.blue800};
      `,
      main: css`
        color: ${colors.white};
      `,
    },
    teaserCard: {
      news: {
        content: css`
          border-color: ${colors.nightBlue700};
        `,
      },
    },
    slider: css`
      .swiper {
        .swiper-pagination {
          .swiper-pagination-bullet {
            border: ${rem(1)} solid ${colors.blue800};
            background: ${colors.white};

            &-active {
              background: ${colors.blue800};
            }
          }
        }
      }
    `,
    modal: {
      dialog: css`
        background: ${colors.blue100};
      `,
    },
    counter: {
      item: css`
        border-color: ${colors.nightBlue700};
        color: ${colors.nightBlue700};
      `,
    },
    accordion: {
      accordionItem: css`
        border-bottom-color: ${colors.nightBlue700};
      `,
    },
    imageWithCaption: {
      caption: css`
        color: ${colors.blue900};
      `,
    },
    contactBox: {
      content: css`
        box-shadow: none;
        border: ${rem(1)} solid ${colors.nightBlue700};
      `,
    },
  },
  'fahr-mit-dem-strom': {
    global: css`
      h1,
      h2,
      h3 {
        color: ${colors.primary};
      }
    `,
    typography: {
      h1: css`
        font-weight: ${fontWeights.black};
      `,
      h2: css`
        font-weight: ${fontWeights.black};
      `,
      h3: css`
        font-weight: ${fontWeights.black};

        article & {
          font-weight: ${fontWeights.medium};
        }
      `,
    },
    link: {
      tile: css`
        background-color: ${colors.greenLight};
      `,
    },
    fact: {
      fact: css`
        color: #252525;

        h3 {
          color: ${colors.black};

          ${mixins.responsiveStyles({
            'font-size': { zero: 18, medium: 24 },
            'line-height': { zero: 22, medium: 34 },
          })}
        }

        p {
          color: #252525;

          ${mixins.responsiveStyles({
            'font-size': { zero: 16, medium: 20 },
            'line-height': { zero: 28, medium: 32 },
          })}
        }

        hr {
          background-color: ${colors.black};
          opacity: 1;
        }
      `,
    },
    teaserTools: {
      tool: css`
        footer a {
          span {
            position: relative;

            &::after {
              content: '';
              border-bottom: ${rem(1)} solid currentColor;
              width: 100%;
              position: absolute;
              left: 0;
              bottom: ${rem(2)};
            }
          }

          svg path {
            stroke: ${colors.white};
          }
        }
      `,
    },
    teaserNavigation: {
      grid: css`
        ${mixins.responsiveStyles({
          'grid-template-columns': {
            zero: 'repeat(2, 1fr)',
            medium: 'repeat(4, 1fr)',
          },
        })}
      `,
    },
  },
  nichtverschwenden: {
    global: css`
      @font-face {
        font-family: 'Futura';
        src: url('/fonts/Futura_Medium.woff') format('woff');
        font-weight: ${fontWeights.medium};
        font-style: normal;
        font-display: swap;
      }

      @font-face {
        font-family: 'Futura';
        src: url('/fonts/Futura_Bold.woff') format('woff');
        font-weight: ${fontWeights.bold};
        font-style: normal;
        font-display: swap;
      }

      @font-face {
        font-family: 'Futura';
        src: url('/fonts/Futura_Medium_Italic.woff') format('woff');
        font-weight: ${fontWeights.medium};
        font-style: italic;
        font-display: swap;
      }

      @font-face {
        font-family: 'Futura';
        src: url('/fonts/Futura_Bold_Italic.woff') format('woff');
        font-weight: ${fontWeights.bold};
        font-style: italic;
        font-display: swap;
      }

      body {
        font-family: 'Futura', sans-serif;
        color: ${colors.grue800};
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        color: ${colors.grue800};
      }
    `,
    typography: {
      h1: css`
        font-weight: ${fontWeights.bold};

        ${mixins.responsiveStyles({
          'font-size': {
            zero: `${rem(24)}`,
            medium: `${rem(32)}`,
          },
          'line-height': {
            zero: `${rem(32)}`,
            medium: `${rem(43)}`,
          },
        })}
      `,
      h2: css`
        font-weight: ${fontWeights.bold};

        ${mixins.responsiveStyles({
          'font-size': {
            zero: `${rem(24)}`,
            medium: `${rem(28)}`,
          },
          'line-height': {
            zero: `${rem(26)}`,
            medium: `${rem(37)}`,
          },
        })}
      `,
      h3: css`
        font-weight: ${fontWeights.bold};
        font-size: ${rem(24)};
        line-height: ${rem(32)};
      `,
    },
    button: {
      default: css`
        background: ${colors.blueNichtverschwenden};
        border: none;
        box-shadow: none;
        overflow: hidden;

        &::before {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          opacity: 0;
          transition: opacity ${animations.default};
          background: linear-gradient(180deg, #fdb013 0%, #fe2508 100%);
        }

        span {
          position: relative;
        }

        &:hover,
        &:focus,
        &:active {
          background: #fdb013;

          &::before {
            opacity: 1;
          }
        }
      `,
      outlined: css`
        background: none;
        border-color: ${colors.blue900};
        color: ${colors.blue900};

        &:hover,
        &:focus,
        &:active {
          border-color: ${colors.blueNichtverschwenden};
          background: ${colors.blueNichtverschwenden};
          color: ${colors.white};
        }
      `,
    },
    publication: {
      info: css`
        ${mixins.responsiveStyles({
          padding: {
            zero: rem(12),
            medium: `${rem(32)} ${rem(24)}`,
          },
        })}
      `,
      link: css`
        font-weight: ${fontWeights.bold};
        color: ${colors.blueNichtverschwenden2};

        ${mixins.responsiveStyles({
          'font-size': {
            zero: rem(12),
            medium: rem(14),
          },
          'line-height': {
            zero: rem(16),
            medium: rem(24),
          },
        })}
      `,
      basket: css`
        svg {
          path,
          polygon {
            stroke: ${colors.blueNichtverschwenden2};
          }
        }
      `,
      format: css`
        font-weight: ${fontWeights.bold};
        padding: ${rem(2)} ${rem(4)};
        color: ${colors.blueNichtverschwenden2};

        ${mixins.responsiveStyles({
          'font-size': {
            zero: rem(12),
            medium: rem(14),
          },
          'line-height': {
            zero: rem(17),
            medium: rem(24),
          },
        })}
      `,
      title: css`
        color: ${colors.grue800};
        font-weight: ${fontWeights.bold};
        margin-bottom: ${rem(10)};

        ${mixins.responsiveStyles({
          'font-size': {
            zero: rem(14),
            medium: rem(18),
          },
          'line-height': {
            zero: rem(19),
            medium: rem(28),
          },
        })}
      `,
      date: css`
        font-weight: ${fontWeights.bold};
        opacity: 0.6;
        color: ${colors.blueNichtverschwenden2};

        ${mixins.responsiveStyles({
          'font-size': {
            zero: rem(12),
            medium: rem(14),
          },
          'line-height': {
            zero: rem(17),
            medium: rem(24),
          },
        })}
      `,
    },
    cookieBanner: {
      paragraph: css`
        font-size: ${rem(20)};
        line-height: ${rem(28)};
        color: ${colors.grue800};

        ${mixins.MQ('medium')} {
          font-size: ${rem(24)};
          line-height: ${rem(32)};
        }
      `,
      blockTitle: css`
        font-weight: ${fontWeights.bold};
      `,
      link: css`
        font-size: ${rem(16)};
        line-height: ${rem(28)};
        color: ${colors.grue800};

        ${mixins.MQ('medium')} {
          font-size: ${rem(24)};
          line-height: ${rem(32)};
        }
      `,
      cookieOption: css`
        input:checked + label::before {
          background: ${colors.blueNichtverschwenden}
            url("data:image/svg+xml,%3Csvg width='12' height='11' viewBox='0 0 12 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.6667 1L4.66671 9L1.33337 6' stroke='white' stroke-width='1.5'/%3E%3C/svg%3E")
            no-repeat center / ${rem(12)} ${rem(11)};
        }

        label {
          color: ${colors.grue800};

          ${mixins.MQ('medium')} {
            font-size: ${rem(24)};
            line-height: ${rem(32)};
          }

          &::before {
            border: ${rem(1)} solid ${colors.blueNichtverschwenden};
          }

          span {
            font-weight: ${fontWeights.bold};
          }
        }
      `,
    },
    fact: {
      fact: css`
        color: ${colors.white};

        h3 {
          color: ${colors.white};

          ${mixins.responsiveStyles({
            'font-size': { zero: 18, medium: 24 },
            'line-height': { zero: 22, medium: 34 },
          })}
        }

        p {
          ${mixins.responsiveStyles({
            'font-size': { zero: 16, medium: 20 },
            'line-height': { zero: 28, medium: 32 },
          })}
        }

        hr {
          background-color: ${colors.white};
          opacity: 1;
        }
      `,
    },
    teaserCard: {
      news: {
        content: css`
          border: none;
          box-shadow: ${shadows.tileShadow};
        `,
      },
    },
    linkContent: {
      link: {
        customColor: (color: string) => css`
          svg {
            path {
              stroke: ${color};
            }
          }
        `,
      },
    },
  },
  ladestationinfrastruktur: {
    global: css`
      @font-face {
        font-family: 'Scto Grotesk A';
        src: local('Scto Grotesk A'),
          url('/fonts/Scto_Grotesk_A_Black.woff') format('woff');
        font-weight: ${fontWeights.black};
        font-style: normal;
        font-display: swap;
      }

      body {
        background-color: ${colors.primarySunYellow100};
        color: ${colors.black};
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      a {
        color: ${colors.secondarySoftPurple800};
      }
    `,
    typography: {
      linkStyles: css`
        &:active,
        &:visited {
          color: ${colors.black};
        }

        &:hover {
          color: ${colors.secondarySoftPurple800};
        }

        &:focus {
          color: ${colors.secondarySoftPurple800};
        }
      `,
    },
    button: {
      default: css`
        background: ${colors.secondarySoftPurple800};
        border: none;
        box-shadow: none;
        overflow: hidden;
        color: ${colors.white};

        &:hover,
        &:focus,
        &:active {
          background: ${colors.primarySunYellow800};
          color: ${colors.secondarySoftPurple800};
        }
      `,
      outlined: css`
        background: none;
        border-color: ${colors.secondarySoftPurple800};
        color: ${colors.secondarySoftPurple800};

        &:hover,
        &:focus,
        &:active {
          border-color: ${colors.secondarySoftPurple800};
          background: ${colors.secondarySoftPurple800};
          color: ${colors.white};
        }
      `,
    },
    breadcrumb: css`
      li {
        a {
          color: ${colors.secondarySoftPurple800};
        }

        &:not(:last-child) {
          &::after {
            color: ${colors.purple400};
          }
        }
      }
    `,
    contactBox: {
      content: css`
        background: ${colors.primarySunYellow700};
      `,
      link: css`
        color: ${colors.secondarySoftPurple800};

        &:hover {
          color: ${colors.secondarySoftPurple800};
        }
      `,
    },
    quote: {
      quote: css`
        color: ${colors.secondarySoftPurple800};
      `,
      caption: css`
        color: ${colors.secondarySoftPurple800};
      `,
    },
    fact: {
      fact: css`
        color: ${colors.black};

        h3 {
          color: ${colors.secondarySoftPurple800};

          ${mixins.responsiveStyles({
            'font-size': { zero: 18, medium: 24 },
            'line-height': { zero: 22, medium: 34 },
          })}
        }

        p {
          color: ${colors.black};

          ${mixins.responsiveStyles({
            'font-size': { zero: 16, medium: 20 },
            'line-height': { zero: 28, medium: 32 },
          })}
        }

        hr {
          background-color: ${colors.black};
          opacity: 1;
        }
      `,
    },
    moduleHeader: {
      mainTitle: css`
        color: ${colors.secondarySoftPurple800};
      `,
      leadText: css`
        color: ${colors.black};
      `,
    },
    accordion: {
      title: css`
        color: ${colors.secondarySoftPurple800};
      `,
      accordionItem: css`
        border-bottom-color: ${colors.secondarySoftPurple400};
      `,
      counter: css`
        &&:after {
          border-color: ${colors.primarySunYellow800};
          color: ${colors.primarySunYellow800};
          font-weight: ${fontWeights.medium};
        }
      `,
      img: css`
        ${mixins.responsiveStyles({
          width: { zero: rem(48), large: rem(48) },
          height: { zero: rem(48), large: rem(48) },
        })}

        margin-top: ${rem(-8)};
        margin-bottom: ${rem(-8)};
      `,
    },
    toggleIcon: {
      icon: css`
        svg path {
          stroke: ${colors.primarySunYellow800};
        }
      `,
    },
    teaserTools: {
      tool: css`
        background: ${colors.secondarySoftPurple800};
      `,
      main: css`
        color: ${colors.white};
        font-size: ${rem(18)};
        line-height: ${rem(26)};
      `,
      title: css`
        hyphens: auto;
      `,
    },
    teaserStorys: {
      title: css`
        hyphens: auto;
      `,
    },
    scrollToTop: {
      button: css`
        color: ${colors.secondarySoftPurple800};
      `,
    },
    styledHr: {
      hr: css`
        background-color: ${colors.secondarySoftPurple200};
      `,
    },
    slider: css`
      .swiper {
        .swiper-pagination {
          .swiper-pagination-bullet {
            border: ${rem(1)} solid ${colors.secondarySoftPurple800};
            background: ${colors.white};

            &-active {
              background: ${colors.secondarySoftPurple800};
            }
          }
        }
      }
    `,
    genericTeaserModule: {
      moreLink: css`
        color: ${colors.secondarySoftPurple800};

        &:hover {
          color: ${colors.secondarySoftPurple800};
        }
      `,
    },
    teaserCard: {
      default: {
        content: css`
          border-color: ${colors.secondarySoftPurple200};
        `,
        date: css`
          color: ${colors.secondarySoftPurple600};
        `,
        link: css`
          color: ${colors.secondarySoftPurple800};

          &:hover {
            color: ${colors.secondarySoftPurple800};
          }
        `,
      },
    },
    teaserNews: {
      background: css`
        background: ${colors.primarySunYellow300};
        opacity: 1;
      `,
      moreLink: css`
        color: ${colors.secondarySoftPurple800};

        &:hover {
          color: ${colors.secondarySoftPurple800};
        }
      `,
    },
    newsletter: {
      content: css`
        color: ${colors.secondarySoftPurple800};
        background-color: ${colors.primarySunYellow700};

        h2 {
          color: inherit;
        }

        canvas {
          display: none;
        }

        a {
          background: ${colors.secondarySoftPurple800};
          color: ${colors.white};

          &:hover,
          &:focus,
          &:active {
            background: ${colors.primarySunYellow800};
            color: ${colors.secondarySoftPurple800};
          }
        }
      `,
    },
    teaserCampaign: {
      lead: css`
        font-weight: ${fontWeights.medium};
      `,
      link: css`
        color: ${colors.secondarySoftPurple800};

        &:hover,
        &:focus {
          color: ${colors.secondarySoftPurple800};

          .label::after {
            border-bottom: ${rem(1)} solid currentColor;
          }
        }

        .m-richtext & {
          text-decoration: none;
        }
      `,
    },
    searchToolbar: {
      button: css`
        svg path {
          transition: stroke ${animations.default};
        }

        &:hover,
        &:focus {
          svg path {
            stroke: ${colors.secondarySoftPurple800};
          }
        }
      `,
      input: css`
        input {
          border-color: transparent;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
      `,
    },
    navSearch: {
      button: (hamburgerActive: boolean) => css`
        color: ${colors.secondarySoftPurple800};

        svg path {
          stroke: ${colors.secondarySoftPurple800};
        }

        &:hover,
        &:focus {
          color: ${colors.secondarySoftPurple800};

          .label {
            opacity: 0.7;

            &::after {
              content: none;
            }
          }
        }

        ${hamburgerActive &&
        css`
          color: ${colors.white};

          &:hover,
          &:focus {
            color: ${colors.white};
          }

          svg path {
            stroke: ${colors.white};
          }
        `}
      `,
      searchToolbar: (hamburgerActive: boolean) => css`
        ${mixins.responsiveStyles({
          top: { zero: rem(0), medium: rem(2) },
          right: { zero: rem(-1), medium: rem(1) },
        })}

        input {
          border-color: ${colors.secondarySoftPurple200};
        }

        ${hamburgerActive &&
        css`
          button {
            background: ${colors.secondarySoftPurple600};
          }
        `}
      `,
    },
    event: {
      event: css`
        border-color: ${colors.secondarySoftPurple200};
        color: ${colors.secondarySoftPurple800};
      `,
    },
    imageWithCaption: {
      caption: css`
        color: ${colors.blue900};
      `,
    },
  },
};

const getStylesExtension = (key?: string) => {
  const styles = stylesObj[key as keyof typeof stylesObj];

  return styles ? { stylesExtension: styles } : {};
};

export default getStylesExtension;
